<template>
    <div class="multi-voice">
        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-header">
                        MultiVoice
                        <br>
                        <small>Export and/or set multiple invoices as sent</small>
                    </div>
                    <div class="card-body">
                        <b-table ref="table" selectable :items="invoiceProvider" :fields="fields" select-mode="range"
                                 @row-selected="onRowSelected" :busy.sync="isBusy" hover
                                 :responsive="true">
                            <template v-slot:head(selected)="header">
                                <div class="form-check">
                                    <div class="checkbox-container">
                                        <i class="material-icons" v-if="allRowsSelected"
                                           @click.prevent="header.clearSelected()">check</i>
                                        <i class="material-icons" v-else @click.prevent="header.selectAllRows()"></i>
                                    </div>
                                </div>
                            </template>
                            <template v-slot:cell(selected)="{ rowSelected }">
                                <div class="form-check">
                                    <div class="checkbox-container">
                                        <i class="material-icons" v-show="rowSelected">check</i>
                                    </div>
                                </div>
                            </template>
                            <template v-slot:cell(user.validBankAccount)="data">
                                <ion-icon v-if="data.value" name="checkmark-circle-outline"
                                          class="text-success"></ion-icon>
                                <ion-icon v-else name="remove-circle-outline" class="text-danger"></ion-icon>
                            </template>
                        </b-table>
                    </div>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-sm-6">
                <div class="card">
                    <div class="card-header">
                        Export to excel
                    </div>
                    <div class="card-body">
                        <p class="card-text">Export selected invoices to excel. For easier manual processing and
                            payments.</p>
                        <button class="btn btn-primary" @click.prevent="exportInvoices" :disabled="selected.length < 1">
                            export
                        </button>
                    </div>
                </div>
            </div>
            <div class="col-sm-6">
                <div class="card">
                    <div class="card-header">
                        Send selected invoices
                    </div>
                    <div class="card-body">
                        <p class="card-text">After "exported" data has been processed and payments made to the accounts,
                            mark those and "send".</p>
                        <div class="row">
                            <div class="col-12">
                                <confirm-toggle v-model="accepted" :label="toggleLabel"></confirm-toggle>
                            </div>
                            <div class="col-12">
                                <button type="submit" class="btn btn-primary btn-block" @click.prevent="send"
                                        :disabled="!accepted">Mark as payment sent
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
  import axios from 'axios';
  import get from 'lodash/get';
  import AxiosInterceptor from './AxiosInterceptor';

  export default {
    name: 'MultiVoice',
    props: {
      url: String,
    },
    data() {
      return {
        invoices: [],
        fields: [
          {key: 'selected', label: 'Selected'},
          {key: 'id', label: 'ID'},
          {key: 'user.name', label: 'Name'},
          {key: 'user.email', label: 'Email'},
          {key: 'sum', label: 'Sum'},
          {key: 'currency', label: 'Currency'},
          {key: 'user.validBankAccount', label: 'BankAccount'},
        ],
        selected: [],
        isBusy: false,
        toggleLabel: 'I have created the selected payments to their respective talents',
        accepted: false,
        locked: false,
      };
    },
    computed: {
      allRowsSelected() {
        return (this.selected.length === this.invoices.length) && (this.invoices.length > 0);
      },
    },
    methods: {
      invoiceProvider(ctx) {
        this.isBusy = true;
        const url = this.url.replace(/^https?:/, '');
        const promise = axios.get(url);

        return promise.then(response => {
          const invoices = response.data || [];
          this.invoices = invoices;
          this.isBusy = false;

          return invoices;
        }).catch(error => {
          this.$toasted.error('Error fetching invoices.');
          this.isBusy = false;

          return [];
        });
      },
      send() {
        if (this.locked) {
          return;
        }
        this.lock();
        this.isBusy = true;

        axios.patch('/admin/invoices', {
          transition: 'App\\States\\Invoice\\PaymentSent',
          invoices: this.selected,
        }).then(response => {
          const count = response.data.length;
          this.$toasted.success(`${count} invoices updated.`);
        }).catch(error => {
          const status = get(error, 'response.status');
          let message = 'Error updating invoices.';
          if (422 === status) {
            message = get(error, 'response.data.message', message);
          }
          this.$toasted.error(message);
        }).finally(() => {
          this.loaded();
          this.unlock();
        });
      },
      loaded() {
        this.isBusy = false;
        this.$nextTick(() => {
          this.$refs.table.refresh();
        });
      },
      onRowSelected(items) {
        this.selected = items.map(item => ({id: item.id}));
      },
      exportInvoices() {
        const data = {
          invoices: this.selected,
        };
        const config = {
          responseType: 'blob',
        };
        const interceptor = AxiosInterceptor.errorAsJson();

        axios.post('/admin/invoices/payment-received/export', data, config).then(response => {
          const fileName = response.headers['content-disposition'].split('=')[1] || 'payments_export.xlsx';
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
        }).catch(error => {
          const message = get(error, 'response.data.message', error.message);
          this.$toasted.error(message);
        }).finally(() => {
          axios.interceptors.request.eject(interceptor);
        });
      },
      lock() {
        this.locked = true;
      },
      unlock() {
        this.locked = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
    @import "../../sass/_variables.scss";

    .multi-voice ::v-deep {
        table {
            ion-icon {
                vertical-align: middle;
                font-size: 24px;
            }

            &.table {
                thead, tbody {
                    tr {
                        th {
                            &:first-child {
                                width: 50px;
                            }
                        }

                        td {
                            vertical-align: middle;
                        }

                        &.table-active:hover td {
                            color: $table-content-text-color;
                        }

                        &:focus {
                            outline: black auto 1px;
                        }
                    }
                }
            }

            .form-check {
                .checkbox-container {
                    cursor: pointer;
                    background: white;
                    width: 25px;
                    height: 25px;

                    i {
                        color: $table-content-text-color;
                        width: 100%;
                        height: 100%;
                    }
                }
            }
        }
    }
</style>
